<script setup lang="ts">
import {
  OctagonCheckIcon,
  OctagonCrossIcon,
  OctagonExclamationIcon,
  OctagonInfoIcon
} from '@/icons';
import { watch, computed } from 'vue';

interface Props {
  message?: string;
  type?: 'error' | 'warning' | 'info' | 'success' | 'loading';
  showIcon?: boolean;
  dismissible?: boolean;
  parentClass?: string;
  autoDismiss?: number | false;
}

const { 
  message = '',
  type = 'error',
  showIcon = true,
  dismissible = false,
  parentClass = '',
  autoDismiss = false
} = defineProps<Props>();

const emit = defineEmits<{
  dismiss: [];
}>();

const handleDismiss = () => {
  emit('dismiss');
};

// Computed properties for cleaner template
const containerClasses = computed(() => {
  const baseClasses = 'p-3 rounded-md border text-sm transition-all duration-200';
  
  switch (type) {
    case 'error':
      return `${baseClasses} bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300`;
    case 'warning':
      return `${baseClasses} bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300`;
    case 'info':
    case 'loading':
      return `${baseClasses} bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300`;
    case 'success':
      return `${baseClasses} bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300`;
    default:
      return baseClasses;
  }
});

const iconClasses = computed(() => {
  switch (type) {
    case 'error':
      return 'text-red-400';
    case 'warning':
      return 'text-yellow-400';
    case 'info':
    case 'loading':
      return 'text-blue-400';
    case 'success':
      return 'text-green-400';
    default:
      return '';
  }
});

const buttonClasses = computed(() => {
  switch (type) {
    case 'error':
      return 'hover:bg-red-200 dark:hover:bg-red-800';
    case 'warning':
      return 'hover:bg-yellow-200 dark:hover:bg-yellow-800';
    case 'info':
    case 'loading':
      return 'hover:bg-blue-200 dark:hover:bg-blue-800';
    case 'success':
      return 'hover:bg-green-200 dark:hover:bg-green-800';
    default:
      return '';
  }
});

// Auto-dismiss functionality
watch(() => message, (newMessage) => {
  if (newMessage && autoDismiss && type !== 'loading') {
    setTimeout(() => {
      if (message === newMessage) {
        handleDismiss();
      }
    }, autoDismiss);
  }
}, { immediate: true });
</script>

<template>
  <div
    v-if="message"
    :class="[containerClasses, parentClass]"
    role="alert"
  >
    <div class="flex items-center gap-2">
      <!-- Icon -->
      <div
        v-if="showIcon"
        :class="['flex-shrink-0 mt-0.5 w-6 h-6', iconClasses]"
      >
        <!-- Loading Spinner -->
        <div v-if="type === 'loading'" class="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"></div>
        <OctagonCheckIcon v-else-if="type === 'success'" />
        <OctagonCrossIcon v-else-if="type === 'error'" />
        <OctagonInfoIcon v-else-if="type === 'info'" />
        <OctagonExclamationIcon v-else-if="type === 'warning'" />
      </div>

      <!-- Message -->
      <div class="flex-1">
        {{ message }}
      </div>

      <!-- Dismiss Button -->
      <button
        v-if="dismissible && type !== 'loading'"
        @click="handleDismiss"
        :class="['flex-shrink-0 p-0.5 rounded hover:bg-opacity-20 transition-colors', buttonClasses]"
        type="button"
        aria-label="Dismiss message"
      >
        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>
  </div>
</template>



<!-- 
<GlobalFormMessage
  v-if="globalMessage"
  :message="globalMessage.message"
  :type="globalMessage.type"
  :dismissible="globalMessage.dismissible"
  :auto-dismiss="globalMessage.autoDismiss"
  @dismiss="clearMessage"
/> -->